import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, DollarSign, TrendingUp, Clock, FileText, Send } from "lucide-react";
import EntityLink from "../components/shared/EntityLink";
import EntityNotes from "../components/shared/EntityNotes";
import { Entity360Shell } from "../components/shared/OSComponents";
import { fmtUSD } from "../utils/currency";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Distribution360Page({ distributionId, onBack, breadcrumbs = [] }) {
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/entity/distribution/${distributionId}`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [distributionId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!data) return <div className="text-center py-20 text-sm text-[#888]" data-testid="distribution-not-found">Distribution not found</div>;

  const { entity, kpis: kpiData, relationships, timeline, permissions } = data;
  const crumbs = [...breadcrumbs, { type: "distribution", id: distributionId, label: `Distribution ${distributionId.slice(0, 8)}` }];

  const relatedEntities = [];
  if (relationships.investor) {
    relatedEntities.push({ type: "investor", id: relationships.investor.entity_id, title: relationships.investor.label, subtitle: relationships.investor.sublabel });
  }
  if (relationships.commitment) {
    relatedEntities.push({ type: "commitment", id: relationships.commitment.entity_id, title: `Commitment: ${relationships.commitment.label}`, subtitle: relationships.commitment.sublabel });
  }
  if (relationships.deal) {
    relatedEntities.push({ type: "deal", id: relationships.deal.entity_id, title: relationships.deal.label, subtitle: relationships.deal.sublabel });
  }
  if (relationships.opportunity) {
    relatedEntities.push({ type: "opportunity", id: relationships.opportunity.entity_id, title: `Opp: ${relationships.opportunity.label}`, subtitle: relationships.opportunity.sublabel });
  }

  const totalCents = kpiData.total_distribution || 0;
  const principalCents = kpiData.principal_returned || 0;
  const interestCents = kpiData.interest_earned || 0;

  const kpis = [
    { label: "Total Distribution", value: fmtUSD(totalCents / 100), icon: DollarSign, accent: true },
    { label: "Principal Returned", value: fmtUSD(principalCents / 100), icon: TrendingUp },
    { label: "Interest Earned", value: fmtUSD(interestCents / 100), icon: TrendingUp },
    { label: "Status", value: (kpiData.status || "—").replace(/_/g, " "), icon: Clock },
  ];

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "related", label: `Related (${relatedEntities.length})` },
    { key: "notes", label: "Notes" },
  ];

  return (
    <Entity360Shell type="distribution" title={`Distribution ${distributionId.slice(0, 8)}`}
      subtitle={`${fmtUSD(totalCents / 100)} total`} status={entity.status}
      kpis={kpis} tabs={tabs} activeTab={tab} onTabChange={setTab}
      onBack={onBack} breadcrumbs={crumbs} onNavigate={navigateToEntity} relatedEntities={relatedEntities}>

      {tab === "overview" && (
        <div className="space-y-4" data-testid="distribution-overview">
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
            <h3 className="text-xs font-bold text-[#222] mb-3">Distribution Details</h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-[#999]">Total:</span> <span className="font-medium text-[#333]">{fmtUSD(totalCents / 100)}</span></div>
              <div><span className="text-[#999]">Principal:</span> <span className="font-medium text-[#333]">{fmtUSD(principalCents / 100)}</span></div>
              <div><span className="text-[#999]">Interest:</span> <span className="font-medium text-[#333]">{fmtUSD(interestCents / 100)}</span></div>
              <div><span className="text-[#999]">Status:</span> <span className="font-medium text-[#333]">{entity.status?.replace(/_/g, " ") || "—"}</span></div>
              <div><span className="text-[#999]">Created:</span> <span className="font-medium text-[#333]">{entity.created_at ? new Date(entity.created_at).toLocaleString() : "—"}</span></div>
              {entity.notes && <div className="col-span-2"><span className="text-[#999]">Notes:</span> <span className="font-medium text-[#333]">{entity.notes}</span></div>}
            </div>
          </div>

          {relationships.investor && (
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
              <h3 className="text-xs font-bold text-[#222] mb-2">Investor</h3>
              <EntityLink type="investor" id={relationships.investor.entity_id}
                label={relationships.investor.label} sublabel={relationships.investor.sublabel} onClick={navigateToEntity} />
            </div>
          )}

          {relationships.commitment && (
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
              <h3 className="text-xs font-bold text-[#222] mb-2">Source Commitment</h3>
              <EntityLink type="commitment" id={relationships.commitment.entity_id}
                label={relationships.commitment.label} sublabel={relationships.commitment.sublabel} onClick={navigateToEntity} />
            </div>
          )}
        </div>
      )}

      {tab === "related" && (
        <div className="space-y-2" data-testid="distribution-related">
          {relatedEntities.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#888]">No related entities</div>
          ) : relatedEntities.map((e, i) => (
            <div key={`${e.type}-${e.id}-${i}`} className="rounded-lg border border-[#e8e0d0] bg-white p-3 flex items-center justify-between hover:shadow-sm transition-shadow">
              <EntityLink type={e.type} id={e.id} label={e.title} sublabel={e.subtitle} onClick={navigateToEntity} />
              <span className="text-[10px] text-[#999] capitalize">{e.type}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "notes" && (
        <EntityNotes entityType="distribution" entityId={distributionId} onNavigate={navigateToEntity} />
      )}
    </Entity360Shell>
  );
}
